<template>
  <!-- A partition: its details and who has access (US 9365) -->
  <div class="us-page">
    <section class="home-hero us-hero pt-hero">
      <div class="home-hero__main">
        <router-link :to="{ name: 'maintenance.partitions' }" class="home-hero__eyebrow pt-back" qid="partition-back-link">
          <font-awesome-icon icon="angle-left" class="mr-1"/>{{ $t('maintenance.partitions.title') }} · {{ getClient.name }}
        </router-link>
        <div class="pt-hero__title">
          <WorkspaceAvatar :workspace="partition" :size="52" :partition="true"/>
          <h1 class="home-hero__title" qid="clients-members-page-title">{{ partition.name }}</h1>
        </div>
        <div class="pt-facts">
          <span v-if="partition.cvr"><b>{{ $t('administration.partitions.create.cvr_label') }}</b> {{ partition.cvr }}</span>
          <span v-if="partition.reference_number"><b>{{ $t('administration.partitions.create.reference_label') }}</b> {{ partition.reference_number }}</span>
          <span v-if="address">
            <img v-if="flag"
                 :src="flag"
                 height="12"
                 alt=""
                 class="mr-1"
            >{{ address }}
          </span>
          <span v-if="!partition.cvr && !partition.reference_number && !address" class="pt-facts__empty">{{ $t('partitions_page.no_details') }}</span>
        </div>
        <div class="home-hero__chips">
          <button v-if="isGranted(permissions.PARTITION_EDIT)"
                  type="button"
                  class="home-chip"
                  qid="partition-edit-button"
                  @click="$refs.form.showEditPartitionModal(partition)"
          >
            <font-awesome-icon icon="pen"/>{{ $t('partitions_page.actions.edit_details') }}
          </button>
        </div>
      </div>

      <div class="home-week us-health" qid="partition-member-stats">
        <div class="home-week__title">{{ $tc('partitions_page.members_count', totalRows, { count: totalRows }) }}</div>
        <div class="home-week__stats">
          <div v-for="role in roleStats" :key="role.name">
            <b>{{ role.count }}</b>{{ role.label }}
          </div>
        </div>
      </div>
    </section>

    <!-- Add a member: pick a user from this client and a role -->
    <div v-if="isGranted(permissions.PARTITION_ADD_USER)" class="v2-card pt-add" qid="partition-add-member">
      <span class="pt-add__label"><font-awesome-icon icon="users" class="mr-2"/>{{ $t('partitions_page.add.title') }}</span>
      <multiselect v-model="selectedUser"
                   class="pt-add__user"
                   :options="options"
                   track-by="slug"
                   label="name"
                   :internal-search="false"
                   :placeholder="$t('partitions_page.add.user_placeholder')"
                   :select-label="$t('system.dropdown_select_label')"
                   :selected-label="$t('system.dropdown_selected_label')"
                   :deselect-label="$t('system.dropdown_deselect_label')"
                   qid="clients-members-user-dropdown"
                   @search-change="findUsers"
      >
        <template slot="singleLabel" slot-scope="props">
          <span class="us-avatar us-avatar--xs" :style="{ backgroundImage: 'url(' + avatarUrl(props.option) + ')' }"></span>
          <span class="ml-2">{{ props.option.name }}</span>
        </template>
        <template slot="option" slot-scope="props">
          <span class="us-avatar us-avatar--xs" :style="{ backgroundImage: 'url(' + avatarUrl(props.option) + ')' }"></span>
          <span class="ml-2">{{ props.option.name }}</span>
          <span class="pt-option-email">{{ props.option.email }}</span>
        </template>
        <template slot="noResult">{{ $t('system.no_results') }}</template>
      </multiselect>
      <multiselect v-model="selectedRole"
                   class="pt-add__role"
                   :options="roles"
                   track-by="id"
                   label="label"
                   :allow-empty="false"
                   :placeholder="$t('administration.partitions.members.select_role_placeholder')"
                   :select-label="$t('system.dropdown_select_label')"
                   :selected-label="$t('system.dropdown_selected_label')"
                   :deselect-label="$t('system.dropdown_deselect_label')"
                   qid="clients-members-role-dropdown"
      ></multiselect>
      <b-button variant="success"
                :disabled="!selectedUser || adding"
                qid="create-user-submit-button"
                @click="addUser"
      >
        <font-awesome-icon :icon="adding ? 'spinner' : 'plus'" :class="{ 'fa-spin': adding }" class="mr-1"/>{{ $t('administration.partitions.members.add') }}
      </b-button>
    </div>

    <div class="v2-card us-card">
      <div class="us-table-wrap">
        <table class="us-table">
          <thead>
            <tr>
              <th>{{ $t('users_page.table.name') }}</th>
              <th>{{ $t('users_page.table.email') }}</th>
              <th>{{ $t('users_page.table.role') }}</th>
              <th>{{ $t('users_page.table.sign_in') }}</th>
              <th class="us-table__last">{{ $t('users_page.table.last_login_at') }}</th>
              <th class="us-table__actions"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in tableItems" :key="user.id" qid="partition-member-row">
              <td>
                <div class="us-who">
                  <span class="us-avatar" :style="{ backgroundImage: 'url(' + avatarUrl(user, true) + ')' }"></span>
                  <span class="us-who__name" qid="client-members-name">{{ user.name }}</span>
                </div>
              </td>
              <td class="us-muted">{{ user.email }}</td>
              <td>
                <span v-if="roleOf(user)" class="v2-tag" :class="roleTag(roleOf(user))">
                  {{ $t('maintenance.users.role.' + $options.filters.slugify(roleOf(user).name)) }}
                </span>
              </td>
              <td>
                <span class="us-method">
                  <SignInMethodIcon :method="user.authentication || 'Application'"/>
                  {{ $t('users_page.sign_in.' + (user.authentication || 'Application')) }}
                </span>
              </td>
              <td>
                <div v-if="user.last_login_at" class="us-last">
                  <span class="us-last__date">{{ formatDateTime(user.last_login_at) }}</span>
                  <span class="us-last__ago">{{ user.last_login_at | formatRelativeDate }}</span>
                </div>
                <span v-else class="v2-tag us-invite--outline">{{ $t('partitions_page.never_signed_in') }}</span>
              </td>
              <td class="us-table__actions">
                <button v-if="isGranted(permissions.PARTITION_REMOVE_USER)"
                        type="button"
                        class="us-icon-btn"
                        v-b-tooltip.hover
                        :title="$t('partitions_page.actions.remove_member')"
                        :aria-label="$t('partitions_page.actions.remove_member')"
                        qid="client-members-delete-button"
                        @click="showConfirmDeleteModal(user)"
                >
                  <font-awesome-icon icon="trash-alt"/>
                </button>
              </td>
            </tr>
            <tr v-if="!loading && !tableItems.length">
              <td colspan="6" class="us-empty">{{ $t('partitions_page.no_members') }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="totalRows > Number(selectedPerPage)" class="us-foot">
        <span class="us-foot__count">{{ $t('users_page.pagination', { from: rangeFrom, to: rangeTo, total: totalRows }) }}</span>
        <span class="us-grow"></span>
        <b-pagination v-model="currentPage"
                      class="us-pagination"
                      :total-rows="totalRows"
                      :per-page="Number(selectedPerPage)"
                      limit="5"
                      @change="loadUsers"
        ></b-pagination>
      </div>
    </div>

    <PartitionFormModal ref="form" buttonType="none" @saved="loadPartition"></PartitionFormModal>

    <!-- Remove member -->
    <b-modal ref="remove-member" modal-class="v2-modal" hide-footer>
      <template #modal-header>
        <div class="us-modal-head">
          <h5 class="mb-0" qid="remove-member-title">{{ $t('administration.partitions.members.delete_title') }}</h5>
          <button type="button"
                  class="us-icon-btn"
                  :aria-label="$t('administration.partitions.members.delete_cancel')"
                  @click="hideConfirmDeleteModal"
          >
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>
      <div class="us-remove">
        <span class="us-avatar" :style="{ backgroundImage: 'url(' + avatarUrl(itemToDelete) + ')' }"></span>
        <div>
          <b>{{ itemToDelete.name }}</b>
          <div class="us-muted">{{ itemToDelete.email }}</div>
        </div>
      </div>
      <p class="mt-3 mb-0">{{ $t('users_page.remove.text', { workspace: partition.name }) }}</p>
      <div class="us-modal-foot">
        <span class="us-grow"></span>
        <b-button variant="secondary" qid="remove-member-cancel-button" @click="hideConfirmDeleteModal">
          {{ $t('administration.partitions.members.delete_cancel') }}
        </b-button>
        <b-button variant="danger" qid="remove-member-submit-button" @click="confirmRemoveMember(itemToDelete)">
          {{ $t('partitions_page.actions.remove_member') }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import {config} from '@/config.js';
  import {userAvatarUrl} from '@/util/userAvatar.js';
  import WorkspaceAvatar from '@/components/navigation/WorkspaceAvatar.vue';
  import SignInMethodIcon from '@/components/user/SignInMethodIcon.vue';
  import PartitionFormModal from '@/components/partition/PartitionFormModal.vue';

  // Roles that can be given in a partition, in the order they are offered
  const PARTITION_ROLES = ['Member', 'Validator', 'Partition admin'];
  const ROLE_TAGS = {
    'Partition admin': 'v2-tag--violet',
    'Validator': 'v2-tag--blue'
  };

  export default {
    name: 'PartitionMembers',
    components: {
      WorkspaceAvatar,
      SignInMethodIcon,
      PartitionFormModal
    },
    data: function() {
      return {
        partition: {},
        tableItems: [],
        countries: [],
        roles: [],
        options: [],
        selectedUser: null,
        selectedRole: null,
        itemToDelete: {},
        adding: false,
        loading: true,
        selectedPerPage: '50',
        currentPage: 1,
        totalRows: 0
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client;
      },
      address() {
        let p = this.partition;
        return [p.street, [p.postcode, p.city].filter(Boolean).join(' '), p.country].filter(Boolean).join(', ');
      },
      flag() {
        let country = this.partition.country && _.find(this.countries, { name: this.partition.country });
        return country ? country.flag : null;
      },
      // Members per role on this page, for the hero
      roleStats() {
        return PARTITION_ROLES.map(name => ({
          name: name,
          label: this.$t('maintenance.users.role.' + this.$options.filters.slugify(name)),
          count: this.tableItems.filter(user => { let role = this.roleOf(user); return role && role.name === name; }).length
        }));
      },
      rangeFrom() {
        return (this.currentPage - 1) * Number(this.selectedPerPage) + 1;
      },
      rangeTo() {
        return Math.min(this.rangeFrom + Number(this.selectedPerPage) - 1, this.totalRows);
      }
    },
    methods: {
      avatarUrl(user, large) {
        return userAvatarUrl(user, large);
      },
      formatDateTime(date) {
        return this.$options.filters.formatDateTime(date, config.dateFormat + ' HH:mm');
      },
      roleOf(user) {
        return _.find(user.userRoles, { client: this.partition.id });
      },
      roleTag(role) {
        return ROLE_TAGS[role.name] || 'v2-tag--grey';
      },
      loadPartition() {
        let self = this;

        piincHttp.get('partitions/' + this.$route.params.slug).then(function(response) {
          self.partition = response.data;
          self.roles = _.sortBy(_.filter(response.data.clientRoles, role => PARTITION_ROLES.indexOf(role.name) !== -1), role => PARTITION_ROLES.indexOf(role.name))
            .map(role => Object.assign({}, role, { label: self.$t('maintenance.users.role.' + self.$options.filters.slugify(role.name)) }));
          self.selectedRole = _.find(self.roles, { name: 'Member' }) || null;
          self.findUsers();
        }, function() {});
      },
      loadUsers(page) {
        let self = this;
        this.currentPage = page || 1;

        piincHttp.get('partitions/' + this.$route.params.slug + '/users', { params: {
          page: this.currentPage,
          per_page: this.selectedPerPage,
          client: this.getClient.slug
        }}).then(function(response) {
          self.tableItems = response.data.items;
          self.totalRows = response.data.total_count;
          self.loading = false;
        }, function() {});
      },
      // Users of this client who aren't in the partition yet
      findUsers: _.debounce(function(query) {
        let self = this;
        if (!this.partition.slug) {
          return;
        }

        piincHttp.get('users', { params: {
          keyword: query || undefined,
          not_member_of_client: this.partition.slug,
          client: this.getClient.id,
          per_page: 50
        }}).then(function(response) {
          self.options = response.data.items;
        }, function() {});
      }, 250),
      addUser() {
        let self = this;
        this.adding = true;

        piincHttp.post('partitions/' + this.$route.params.slug + '/users', {
          client: this.partition.slug,
          user: this.selectedUser.slug,
          role: this.selectedRole ? this.selectedRole.id : undefined
        }).then(function() {
          self.adding = false;
          self.$toastr('success', self.$t('partitions_page.add.success', { name: self.selectedUser.name }));
          self.selectedUser = null;
          self.loadUsers();
          self.findUsers();
        }, function() {
          self.adding = false;
        });
      },
      showConfirmDeleteModal(user) {
        this.itemToDelete = user;
        this.$refs['remove-member'].show();
      },
      hideConfirmDeleteModal() {
        this.$refs['remove-member'].hide();
      },
      confirmRemoveMember(user) {
        let self = this;

        piincHttp.delete('partitions/' + this.$route.params.slug + '/users/' + user.slug).then(function() {
          self.hideConfirmDeleteModal();
          self.loadUsers(self.currentPage);
          self.findUsers();
          self.$toastr('success', self.$t('administration.partitions.members.delete_success'));
        }, function() {});
      }
    },
    created() {
      let self = this;
      piincHttp.get('countries-with-flag').then(function(response) {
        self.countries = response.data;
      }, function() {});
      this.loadPartition();
      this.loadUsers();
    }
  }
</script>
